import React, { useState } from "react";
import { Box, Typography, Menu, MenuItem, Divider } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DROPDOWN_PAPER_SX, DROPDOWN_ITEM_SX } from "../../../utils/dropdownMenuStyles";

// Matches this app's real dropdown-popup formula EXACTLY — the same numbers as
// MessageCard.jsx's "..." menu / Lifecycle+Assign submenus (Dashboard's
// FilterDropdown.jsx, confirmed standard), shared via dropdownMenuStyles.js so
// the three can't drift apart. First pass here used native
// RadioGroup/FormControlLabel/Radio — Radio's own built-in touch-target padding
// ignored every spacing override, rendering huge oversized rows with a tiny
// header by comparison (Rahim, 2026-08-19: "way too big... idk what you
// followed"). Rebuilt with plain MenuItem rows at the correct spacing, keeping
// a radio-style selection indicator per Rahim's follow-up — but a custom
// RadioDot (below), not MUI's <Radio>, since that component's own padding was
// the actual root cause.
const FILTER_POPOVER_PAPER_SX = { ...DROPDOWN_PAPER_SX, py: 0 };

// Outer padding wrapper (not per-row margin) — same "hover pill floats inset
// from the popup edges" pattern as LeftNav.jsx's rail popups / LifecycleStageMenuList.
const WRAPPER_PADDING = 0.75;
const ROW_INDENT = 1.5;

const FILTER_SECTION_HEADER_SX = {
  pl: ROW_INDENT,
  pr: 1,
  pt: 1,
  pb: 0.5,
  fontSize: "0.7rem",
  fontWeight: 600,
  color: "text.secondary",
};

const FILTER_ITEM_SX = { ...DROPDOWN_ITEM_SX, px: ROW_INDENT };

// Plain styled circle, not MUI's <Radio> — that component's own built-in
// touch-target padding is exactly what broke the spacing on the first pass.
// This is fully sized by our own sx, no hidden padding to fight.
const RadioDot = ({ checked }) => (
  <Box
    sx={{
      width: 16,
      height: 16,
      borderRadius: "50%",
      flexShrink: 0,
      border: (theme) => `2px solid ${checked ? theme.palette.primary.main : theme.palette.text.disabled}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {checked && (
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: (theme) => theme.palette.primary.main,
        }}
      />
    )}
  </Box>
);

/**
 * "{Show label}, {Sort label} ⌄" summary bar that opens a Menu with a
 * "Show" option list and a "Sort by" option list. Parameterized by
 * showOptions/sortOptions ({value, label}[], entries may also be
 * {divider: true} to render a visual separator) so Chats and Calls share
 * this one component with entirely different option sets instead of each
 * forking their own dropdown.
 */
export default function InboxFilterDropdown({ showValue, onShowChange, showOptions, sortValue, onSortChange, sortOptions }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const showLabel = showOptions.find((o) => !o.divider && o.value === showValue)?.label || "All";
  const sortLabel = sortOptions.find((o) => o.value === sortValue)?.label || sortOptions[0]?.label;

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Box
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          cursor: "pointer",
          color: (theme) => theme.palette.text.secondary,
          fontSize: "0.875rem",
          fontWeight: 500,
          userSelect: "none",
        }}
      >
        <Typography component="span" sx={{ fontSize: "inherit", fontWeight: "inherit" }}>
          {showLabel}, {sortLabel}
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 16 }} />
      </Box>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { mt: 0.75, minWidth: 220, ...FILTER_POPOVER_PAPER_SX },
          },
        }}
      >
        <Box sx={{ p: WRAPPER_PADDING }}>
          <Typography sx={FILTER_SECTION_HEADER_SX}>Show</Typography>
          {showOptions.map((opt, i) =>
            opt.divider ? (
              <Divider key={`divider-${i}`} sx={{ my: 0.5 }} />
            ) : (
              <MenuItem
                key={opt.value ?? "all"}
                onClick={() => {
                  onShowChange(opt.value ?? "all");
                  handleClose();
                }}
                dense
                sx={FILTER_ITEM_SX}
              >
                <RadioDot checked={(opt.value ?? "all") === (showValue ?? "all")} />
                {opt.label}
              </MenuItem>
            )
          )}

          <Typography sx={{ ...FILTER_SECTION_HEADER_SX, mt: 0.5 }}>Sort by</Typography>
          {sortOptions.map((opt) => (
            <MenuItem
              key={opt.value}
              onClick={() => {
                onSortChange(opt.value);
                handleClose();
              }}
              dense
              sx={FILTER_ITEM_SX}
            >
              <RadioDot checked={opt.value === sortValue} />
              {opt.label}
            </MenuItem>
          ))}
        </Box>
      </Menu>
    </>
  );
}
